import { FormApi, FormState } from 'final-form';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { toast } from 'services';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Co2FootprintVO } from 'vo';
import { Button } from '@net0-calc/common/components/ui';
import { RedArrowCircleIcon } from '@net0-calc/common/assets/svg';
import { DEVICE } from '@net0-calc/common/assets/styles';
import { monacoButtonStyles } from 'assets/buttonStyles';
import { WizardPageProps } from '@net0-calc/common/types';
// import { CO2Footprint } from 'components';

const handleError = (error: any) => {
  let message = 'Something went wrong. Please try again later.';
  if (error.response?.data?.messages) {
    message = error.response?.data?.messages.join(', ');
  } else if (error.message) {
    message = error.message;
  }

  toast.error(message);
};

interface WizardFormProps<T> {
  pages: ReactElement<WizardPageProps<T>>[];
  currentPageIndex: number;
  onStepSubmit: (values: T) => Promise<void>;
  initialValues: T;
  co2FootprintSumVO: Co2FootprintVO;
  onChangeFormSpy?: (state: FormState<T>) => void;
}

const SWIP_ANIMATION_DURATION_MS = 500;

export function WizardForm<T>({
  pages,
  currentPageIndex,
  onStepSubmit,
  initialValues,
  onChangeFormSpy,
}: WizardFormProps<T>) {
  const currentPage = pages[currentPageIndex];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const [isSwipAnimationEnabled, setIsSwipAnimationEnabled] = useState(false);
  const enableSwipAnimation = () => setIsSwipAnimationEnabled(true);
  const disableSwipAnimation = () => setIsSwipAnimationEnabled(false);

  const handleContinue = (values: T, hasErrors: boolean, form: FormApi<T, Partial<T>>) => async () => {
    // If there are validation errors, touch all fields to show errors
    if (hasErrors) {
      const registeredFields = form.getRegisteredFields();
      registeredFields.forEach(fieldName => {
        form.blur(fieldName as keyof T);
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onStepSubmit(values);

      enableSwipAnimation();
      setTimeout(() => {
        disableSwipAnimation();
      }, SWIP_ANIMATION_DURATION_MS);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonTitle = useMemo(() => {
    if (isSubmitting) {
      return t('general.wizardForm.submitButton.loading');
    }
    if (currentPageIndex === 0) {
      return t('general.wizardForm.submitButton.getStarted');
    }
    return t('general.wizardForm.submitButton.nextStep');
  }, [currentPageIndex, isSubmitting]);

  useEffect(() => {
    !isSwipAnimationEnabled &&
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
  }, [currentPageIndex, isSwipAnimationEnabled]);

  return (
    <Form
      initialValues={initialValues}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSubmit={(_: T) => {}} // onSubmit required, but not used
      render={({ hasValidationErrors, values, form }) => (
        <>
          {onChangeFormSpy && <FormSpy<T> onChange={onChangeFormSpy} />}
          <Root $isAnimationEnabled={isSwipAnimationEnabled} id="page-container">
            {currentPage &&
              React.cloneElement(currentPage, {
                form,
                values,
              })}
            <ButtonWrapper>
              <StyledButton
                isDisabled={isSwipAnimationEnabled || isSubmitting}
                title={buttonTitle}
                type="button"
                variant="primary"
                customVariants={monacoButtonStyles}
                icon={<RedArrowCircleIcon />}
                onClick={handleContinue(values, hasValidationErrors, form)}
              />
            </ButtonWrapper>
          </Root>
        </>
      )}
    />
  );
}

const Root = styled.form<{ $isAnimationEnabled: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  width: 100%;
  height: 100%;

  @media ${DEVICE.laptop} {
    height: auto;
    justify-content: flex-start;
    margin-bottom: 70px;
  }

  @keyframes wizardFormAnimation {
    0% {
      transform: translateX(0) scale(1);
      opacity: 1;
    }
    40% {
      transform: translateX(-8%) scale(0.98);
      opacity: 0;
    }
    60% {
      transform: translateX(6%) scale(0.98);
      opacity: 0;
    }
    100% {
      transform: translateX(0) scale(1);
      opacity: 1;
    }
  }

  ${({ $isAnimationEnabled }) =>
    $isAnimationEnabled &&
    `animation: wizardFormAnimation ${SWIP_ANIMATION_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1) 1;`}
`;
const ButtonWrapper = styled.div`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 0 4px;
  background: linear-gradient(to bottom, transparent 0%, var(--color5) 30%);
  z-index: 10;

  @media ${DEVICE.laptop} {
    position: static;
    padding: 0;
    background: none;
  }
`;
const StyledButton = styled(Button)`
  padding: 13px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  gap: 12px;
`;
